import { ESdChangeReason, IConditionModel, IWFStep } from "@kuika/kuika-cl-model";
import { ConditionSetType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { Comparison } from "../../properties-panel/condition";
import {
  getBpmnLoopState,
  readLoopCondition,
  readLoopMax,
  writeLoopCondition,
  writeLoopMax
} from "../wf-utils/loop-utils";

interface IProps {
  step?: IWFStep;
}
interface LocalState {
  maxIterations?: number;
}

class LoopDecisionPropertiesBase extends PureSdComponent<IProps & IWithSDProps & IWithContextProp, LocalState> {
  constructor(props: IProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  private resolveSelectedStep(): IWFStep | undefined {
    const explicit = this.props.step;
    if (explicit?.id) return explicit;

    const sd: any = (this.props as any).sd;
    const viaApi = sd?.getSelectedWfStep?.();
    if (viaApi?.id) return viaApi;

    const selId = sd?.selectedWfNode?.id;
    const nodes = sd?.selectedWfNode?.nodes;
    if (!selId || !nodes) return undefined;

    const findRec = (n: any, id: string): any | undefined => {
      const steps: any[] = n?.steps ?? [];
      const hit = steps.find((s) => s?.id === id);
      if (hit) return hit;
      for (const s of steps) {
        const inner = s?.subprocess && findRec(s.subprocess, id);
        if (inner) return inner;
      }
      return undefined;
    };
    return findRec(nodes, selId);
  }

  private defaultCondition(): IConditionModel {
    return {
      id: Guid.create().toString(),
      isOrGroup: false,
      conditionSet: { type: ConditionSetType.and, conditions: [] }
    } as IConditionModel;
  }

  private saveCond = _.debounce(async (c: IConditionModel) => {
    const sd = (this.props as any).sd;
    const step = this.resolveSelectedStep();
    if (!sd || !step) return;
    await writeLoopCondition(sd, step, c, ESdChangeReason.WFStepMappingChange);
  }, 150);

  private saveMax = _.debounce(async (max: number) => {
    const sd = (this.props as any).sd;
    const step = this.resolveSelectedStep();
    if (!sd || !step) return;
    await writeLoopMax(sd, step, max, ESdChangeReason.WFStepMappingChange);
  }, 200);

  private onMaxChange = (val: number | null): void => {
    const safe: number = val == null || Number.isNaN(val) ? 0 : Math.max(0, Math.floor(val));
    this.setState({ maxIterations: safe });
    this.saveMax(safe);
  };

  private renderCancelCondition(step: IWFStep) {
    let cond = readLoopCondition(step) as IConditionModel | undefined;
    if (!cond) cond = this.defaultCondition();

    return (
      <section className="kuika-wf-step__section" data-testid="std-loop-condition">
        <div className="kuika-wf-step__section-head">
          <KuikaSvg name="x" width={14} height={14} color="var(--dark-9)" className="kuika-wf-step__section-icon" />
          <span className="kuika-wf-step__section-title">Cancel Condition</span>
        </div>
        <p className="kuika-wf-step__hint">
          The step repeats until this condition is met. Once the condition evaluates to true, the loop exits.
        </p>

        <Comparison
          condition={cond}
          onChange={(c) => this.saveCond(c)}
          conditionType="workflow"
          executePointer={step.name}
          showHeader={false}
        />
      </section>
    );
  }

  private renderIterationLimit(step: IWFStep) {
    const currentMax: number = this.state.maxIterations ?? readLoopMax(step) ?? 0;

    return (
      <section className="kuika-wf-step__section" data-testid="std-loop-max">
        <div className="kuika-wf-step__section-head">
          <KuikaSvg
            name="shield"
            width={14}
            height={14}
            color="var(--dark-9)"
            className="kuika-wf-step__section-icon"
          />
          <span className="kuika-wf-step__section-title">Iteration Limit</span>
        </div>
        <p className="kuika-wf-step__hint">
          Safety guard for the case where the cancel condition is never met. 0 means unlimited.
        </p>

        <div className="kuika-wf-step__field-row">
          <span className="kuika-wf-step__field-label">Max Iterations</span>
          <span className="kuika-wf-step__field-control">
            <KuikaInput
              className="kuika-wf-step__number"
              type="number"
              size="sm"
              min={0}
              step={1}
              precision={0}
              value={currentMax}
              onChange={(_e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string | number): void =>
                this.onMaxChange(value === "" ? null : Number(value))
              }
            />
          </span>
        </div>
      </section>
    );
  }

  render() {
    const step = this.resolveSelectedStep();
    if (!step?.id) return null;

    const loop = getBpmnLoopState(step.id);
    if (!loop.enabled) return null;

    return (
      <>
        {this.renderCancelCondition(step)}
        {this.renderIterationLimit(step)}
      </>
    );
  }

  checkNeedRender = (): boolean => true;
}

const LoopDecisionProperties = withContext<IProps>(withSD<IProps>(LoopDecisionPropertiesBase));
export { LoopDecisionProperties };
